import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  useAddIntranetPostPhoto,
  useCancelIntranetEvent,
  useChooseIntranetEventDate,
  useDeleteIntranetEvent,
  useDeleteIntranetPost,
  useIntranetEventKinds,
  useIntranetGroups,
  useIntranetPostReads,
  useMarkIntranetPostRead,
  useRespondToIntranetEvent,
  useSaveIntranetEvent,
  useSaveIntranetPost,
  useUsers
} from '../api/queries'
import type { IntranetEvent, IntranetPhoto, IntranetPost, IntranetVote, UserRef } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { HireDateInput, useHireDateMap } from './Celebrations'
import { WelcomeChecklistEditor, WelcomeForm } from './IntranetWelcome'
import { useWelcomeStatus } from '../api/queries'
import { useModuleEnabled } from '../navigation'
import { prepareDocumentFile } from '../lib/documentPhoto'
import { formatDate, formatDateTime } from '../lib/format'
import { useAuthedBlob } from '../lib/photos'
import { Avatar, Card } from './ui'

/**
 * Stumperne til intranettet (docs/DECISIONS.md beslutning 100).
 *
 * <b>Der står altid, hvem der har lavet det</b> - med ansigt på. Et opslag uden
 * afsender og en fest uden vært er der ingen, der tager alvorligt.
 */

/** Hvem har skrevet det, og hvornår. Med profilbillede (beslutning 041). */
export function ByLine({ user, at, extra, fallback = 'Ukendt' }: { user?: UserRef | null; at: string; extra?: string; fallback?: string }) {
  return (
    <div className="row" style={{ gap: 8, alignItems: 'center' }}>
      {user && <Avatar user={user} size={28} />}
      <span className="tiny muted">
        {user?.displayName ?? fallback} · {formatDateTime(at)}
        {extra ? ` · ${extra}` : ''}
      </span>
    </div>
  )
}

/** Ét billede fra et opslag. Åbner i fuld størrelse ved tryk. */
export function IntranetPhotoThumb({ photo }: { photo: IntranetPhoto }) {
  const url = useAuthedBlob(photo.url)

  if (!url) {
    return <div style={{ width: 96, height: 96, borderRadius: 8, background: '#F1F5F9' }} />
  }

  return (
    <a href={url} target="_blank" rel="noreferrer" title={photo.caption ?? 'Billede fra opslaget'}>
      <img
        src={url}
        alt={photo.caption ?? 'Billede fra opslaget'}
        style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8 }}
      />
    </a>
  )
}

/**
 * Et opslag, som det står på tavlen. Kræver det en kvittering, står knappen
 * "Jeg har læst det" nederst, til man har trykket.
 */
export function PostCard({ post, onEdit }: { post: IntranetPost; onEdit?: (post: IntranetPost) => void }) {
  const { isIntranetAdmin: isAdmin } = useAuth()
  const markRead = useMarkIntranetPostRead()
  const remove = useDeleteIntranetPost()
  const [showReads, setShowReads] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function run(action: () => Promise<unknown>) {
    setError(null)
    try {
      await action()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Handlingen mislykkedes.')
    }
  }

  return (
    <Card
      title={`${post.isPinned ? '📌 ' : ''}${post.title}`}
      actions={
        isAdmin ? (
          <div className="row" style={{ gap: 6 }}>
            {onEdit && (
              <button className="btn btn--sm" onClick={() => onEdit(post)}>
                Ret
              </button>
            )}
            <button
              className="btn btn--sm"
              disabled={remove.isPending}
              onClick={() => {
                if (window.confirm(`Fjern opslaget "${post.title}"?`)) void run(() => remove.mutateAsync(post.id))
              }}
            >
              Fjern
            </button>
          </div>
        ) : undefined
      }
    >
      {/* Et opslag uden afsender er skrevet af systemet selv - f.eks. "Ugen der gik" (beslutning 126). */}
      <ByLine user={post.createdBy} at={post.createdAt} extra={post.updatedAt ? 'rettet' : undefined} fallback="Overblik" />

      <div className="stack" style={{ gap: 6, marginTop: 10, whiteSpace: 'pre-wrap' }}>
        {post.content}
      </div>

      {post.photos.length > 0 && (
        <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          {post.photos.map((photo) => (
            <IntranetPhotoThumb key={photo.id} photo={photo} />
          ))}
        </div>
      )}

      {post.requiresAck && (
        <div className="row row--between" style={{ marginTop: 12, gap: 8, flexWrap: 'wrap' }}>
          {post.hasRead ? (
            <span className="tiny muted">✓ Du har kvitteret for at have læst det.</span>
          ) : (
            <button
              className="btn btn--sm btn--primary"
              disabled={markRead.isPending}
              onClick={() => void run(() => markRead.mutateAsync(post.id))}
            >
              Jeg har læst det
            </button>
          )}

          {isAdmin && (
            <button className="btn btn--sm" onClick={() => setShowReads((v) => !v)}>
              {post.readCount} af {post.recipientCount} har læst
            </button>
          )}
        </div>
      )}

      {showReads && <PostReads postId={post.id} />}
      {error && <div className="error-box" style={{ marginTop: 8 }}>{error}</div>}
    </Card>
  )
}

/** Hvem har kvitteret, og hvem mangler. Kun kontoret ser den. */
function PostReads({ postId }: { postId: number }) {
  const { data } = useIntranetPostReads(postId)

  if (!data) return <div className="tiny muted" style={{ marginTop: 8 }}>Henter ...</div>

  return (
    <div className="stack" style={{ gap: 4, marginTop: 10 }}>
      <div className="tiny muted">
        <strong>Har læst:</strong> {data.read.map((r) => r.user.displayName).join(', ') || 'ingen endnu'}
      </div>
      <div className="tiny warn">
        <strong>Mangler:</strong> {data.missing.map((u) => u.displayName).join(', ') || 'ingen'}
      </div>
    </div>
  )
}

/** Skriv eller ret et opslag. Kun kontoret. */
export function PostForm({ post, onClose }: { post: IntranetPost | null; onClose: () => void }) {
  const save = useSaveIntranetPost()
  const addPhoto = useAddIntranetPostPhoto()
  const { data: groups } = useIntranetGroups()

  const [title, setTitle] = useState(post?.title ?? '')
  const [content, setContent] = useState(post?.content ?? '')
  const [isPinned, setIsPinned] = useState(post?.isPinned ?? false)
  const [groupId, setGroupId] = useState(post?.groupId ? String(post.groupId) : '')
  const [requiresAck, setRequiresAck] = useState(post?.requiresAck ?? false)
  const [notify, setNotify] = useState(!post)
  const [notifySms, setNotifySms] = useState(false)
  const [files, setFiles] = useState<File[]>([])
  const [error, setError] = useState<string | null>(null)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)

    try {
      const saved = await save.mutateAsync({
        id: post?.id,
        input: {
          title: title.trim(),
          content: content.trim(),
          isPinned,
          groupId: groupId ? Number(groupId) : null,
          requiresAck,
          notify: post ? false : notify,
          notifySms: post ? false : notifySms
        }
      })

      // Opslaget foerst, billederne bagefter - de skal haenge paa noget.
      for (const file of files) {
        const prepared = await prepareDocumentFile(file)
        await addPhoto.mutateAsync({ id: saved.id, file: prepared.blob, fileName: prepared.fileName })
      }

      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaget kunne ikke gemmes.')
    }
  }

  const busy = save.isPending || addPhoto.isPending

  return (
    <Card title={post ? 'Ret opslaget' : 'Nyt opslag'}>
      <form className="stack" onSubmit={submit}>
        <label className="field">
          <span className="field__label">Overskrift *</span>
          <input required maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>

        <label className="field">
          <span className="field__label">Tekst *</span>
          <textarea
            required
            rows={6}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Hvad skal huset vide?"
          />
        </label>

        <label className="field">
          <span className="field__label">Hvem er det til?</span>
          <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            <option value="">Hele huset</option>
            {(groups ?? []).map((group) => (
              <option key={group.id} value={group.id}>
                {group.icon ? `${group.icon} ` : ''}
                {group.name} ({group.memberCount})
              </option>
            ))}
          </select>
          <span className="tiny muted">
            Vælges en arbejdsgruppe, er opslaget kun synligt for dens medlemmer - og kun de får besked.
          </span>
        </label>

        <label className="row small" style={{ gap: 8 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={isPinned}
            onChange={(e) => setIsPinned(e.target.checked)}
          />
          Hæng op øverst
        </label>

        <label className="row small" style={{ gap: 8 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={requiresAck}
            onChange={(e) => setRequiresAck(e.target.checked)}
          />
          Alle skal kvittere for at have læst det
        </label>

        {!post && (
          <>
            <label className="row small" style={{ gap: 8 }}>
              <input
                type="checkbox"
                style={{ width: 'auto' }}
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
              />
              Giv besked i appen
            </label>

            <label className="row small" style={{ gap: 8 }}>
              <input
                type="checkbox"
                style={{ width: 'auto' }}
                disabled={!notify}
                checked={notifySms}
                onChange={(e) => setNotifySms(e.target.checked)}
              />
              Send også som SMS
            </label>
          </>
        )}

        <label className="field">
          <span className="field__label">
            Billeder {files.length > 0 && <span className="tiny muted">({files.length} valgt)</span>}
          </span>
          <input type="file" accept="image/*" multiple onChange={(e) => setFiles(Array.from(e.target.files ?? []))} />
        </label>

        {error && <div className="error-box">{error}</div>}

        <div className="btn-group">
          <button className="btn btn--primary" type="submit" disabled={busy}>
            {busy ? 'Gemmer ...' : post ? 'Gem' : 'Slå op'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Fortryd
          </button>
        </div>
      </form>
    </Card>
  )
}

/** Datoen, som den siges. Hele dage skrives uden klokkeslæt. */
export function eventDateText(date: { startsAt: string; endsAt?: string | null; allDay: boolean }) {
  const start = new Date(date.startsAt)
  const day = start.toLocaleDateString('da-DK', { weekday: 'long', day: 'numeric', month: 'long' })

  if (date.allDay) return day

  const time = start.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })
  const end = date.endsAt ? new Date(date.endsAt) : null
  const endTime = end ? end.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' }) : null

  return endTime ? `${day} kl. ${time}-${endTime}` : `${day} kl. ${time}`
}

/**
 * En begivenhed med afstemning om datoen.
 *
 * <b>Svarene er åbne</b> for de inviterede: man skal kunne se, om det giver
 * mening at flytte sin egen aften. Opretteren vælger datoen til sidst.
 */
export function EventCard({ event, onEdit }: { event: IntranetEvent; onEdit?: (event: IntranetEvent) => void }) {
  const respond = useRespondToIntranetEvent(event.id)
  const choose = useChooseIntranetEventDate(event.id)
  const cancel = useCancelIntranetEvent()
  const remove = useDeleteIntranetEvent()

  const [votes, setVotes] = useState<Record<number, IntranetVote>>(
    Object.fromEntries(event.dates.filter((d) => d.myVote).map((d) => [d.id, d.myVote as IntranetVote]))
  )
  const [guests, setGuests] = useState(String(event.myGuestCount))
  const [note, setNote] = useState(event.myNote ?? '')
  const [error, setError] = useState<string | null>(null)

  const chosen = event.dates.find((d) => d.id === event.chosenDateId)
  const deadlinePassed = event.responseDeadline ? new Date(event.responseDeadline) < new Date() : false
  const canRespond = event.isInvited && !event.isCancelled && !deadlinePassed

  async function run(action: () => Promise<unknown>) {
    setError(null)
    try {
      await action()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Handlingen mislykkedes.')
    }
  }

  return (
    <Card
      title={`${event.kindIcon} ${event.title}${event.isCancelled ? ' - AFLYST' : ''}`}
      actions={
        event.canEdit ? (
          <div className="row" style={{ gap: 6 }}>
            {onEdit && (
              <button className="btn btn--sm" onClick={() => onEdit(event)}>
                Ret
              </button>
            )}
            {!event.isCancelled && (
              <button
                className="btn btn--sm"
                onClick={() => {
                  if (window.confirm(`Aflys "${event.title}"? De inviterede kan stadig se den.`)) {
                    void run(() => cancel.mutateAsync(event.id))
                  }
                }}
              >
                Aflys
              </button>
            )}
            <button
              className="btn btn--sm"
              onClick={() => {
                if (window.confirm(`Slet "${event.title}" helt?`)) void run(() => remove.mutateAsync(event.id))
              }}
            >
              Slet
            </button>
          </div>
        ) : undefined
      }
    >
      <ByLine
        user={event.createdBy}
        at={event.createdAt}
        extra={event.audienceAll ? 'hele huset' : `${event.invitees.length} inviteret`}
      />

      {event.description && (
        <p className="small" style={{ whiteSpace: 'pre-wrap', marginTop: 10 }}>
          {event.description}
        </p>
      )}

      <div className="tiny muted" style={{ marginTop: 6 }}>
        {event.location && <>📍 {event.location} · </>}
        {event.responseDeadline && <>svar senest {formatDate(event.responseDeadline)} · </>}
        {event.responseCount} af {event.recipientCount} har svaret
        {!event.publishedAt && <> · ikke udgivet endnu{event.publishAt ? ` (udgives ${formatDate(event.publishAt)})` : ''}</>}
      </div>

      {chosen ? (
        <div className="small" style={{ marginTop: 12 }}>
          <strong>Datoen er: {eventDateText(chosen)}</strong>
        </div>
      ) : (
        <div className="stack" style={{ gap: 8, marginTop: 12 }}>
          {event.dates.length > 1 && <div className="small">Hvornår kan du?</div>}

          {event.dates.map((date) => (
            <div key={date.id} className="row row--between" style={{ gap: 8, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <div className="small">{eventDateText(date)}</div>
                <div className="tiny muted">
                  {date.yesCount} kan · {date.maybeCount} måske · {date.noCount} kan ikke
                  {date.votes.length > 0 && (
                    <> · {date.votes.map((v) => `${v.user.displayName} (${v.voteName.toLowerCase()})`).join(', ')}</>
                  )}
                </div>
              </div>

              <div className="row" style={{ gap: 4 }}>
                {(['yes', 'maybe', 'no'] as IntranetVote[]).map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={`btn btn--sm ${votes[date.id] === value ? 'btn--primary' : ''}`}
                    disabled={!canRespond}
                    onClick={() => setVotes((current) => ({ ...current, [date.id]: value }))}
                  >
                    {value === 'yes' ? 'Kan' : value === 'maybe' ? 'Måske' : 'Kan ikke'}
                  </button>
                ))}

                {event.canEdit && event.dates.length > 1 && (
                  <button
                    type="button"
                    className="btn btn--sm"
                    disabled={choose.isPending}
                    onClick={() => void run(() => choose.mutateAsync(date.id))}
                  >
                    Vælg denne
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {canRespond && (
        <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label className="field" style={{ maxWidth: 110 }}>
            <span className="field__label">Gæster med</span>
            <input inputMode="numeric" value={guests} onChange={(e) => setGuests(e.target.value)} />
          </label>

          <label className="field" style={{ flex: 1, minWidth: 180 }}>
            <span className="field__label">Bemærkning</span>
            <input
              maxLength={300}
              value={note}
              placeholder="F.eks. kommer lidt senere"
              onChange={(e) => setNote(e.target.value)}
            />
          </label>

          <button
            className="btn btn--primary btn--sm"
            disabled={respond.isPending}
            onClick={() =>
              void run(() =>
                respond.mutateAsync({
                  votes: votes as Record<number, string>,
                  guestCount: Number(guests) || 0,
                  note: note.trim() || null
                })
              )
            }
          >
            {respond.isPending ? 'Gemmer ...' : 'Gem mit svar'}
          </button>
        </div>
      )}

      {deadlinePassed && !event.isCancelled && (
        <div className="tiny warn" style={{ marginTop: 8 }}>
          Fristen for at svare er udløbet.
        </div>
      )}

      {error && <div className="error-box" style={{ marginTop: 8 }}>{error}</div>}
    </Card>
  )
}

/** Opret eller ret en begivenhed. Alle må oprette. */
export function EventForm({ event, onClose }: { event: IntranetEvent | null; onClose: () => void }) {
  const save = useSaveIntranetEvent()
  const { data: kinds } = useIntranetEventKinds()
  const { data: groups } = useIntranetGroups()
  const { data: users } = useUsers()

  const [title, setTitle] = useState(event?.title ?? '')
  const [description, setDescription] = useState(event?.description ?? '')
  const [kind, setKind] = useState(event?.kind ?? 'party')
  const [location, setLocation] = useState(event?.location ?? '')
  const [audienceAll, setAudienceAll] = useState(event?.audienceAll ?? true)
  const [inviteeIds, setInviteeIds] = useState<number[]>(event?.invitees.map((u) => u.id) ?? [])
  const [groupIds, setGroupIds] = useState<number[]>(event?.groups.map((g) => g.id) ?? [])
  const [dates, setDates] = useState<string[]>(
    event?.dates.map((d) => d.startsAt.slice(0, 16)) ?? [defaultDate()]
  )
  const [deadline, setDeadline] = useState(event?.responseDeadline?.slice(0, 10) ?? '')
  const [publishAt, setPublishAt] = useState(event?.publishAt?.slice(0, 10) ?? '')
  const [notifyPush, setNotifyPush] = useState(event?.notifyPush ?? true)
  const [notifySms, setNotifySms] = useState(event?.notifySms ?? false)
  const [reminder, setReminder] = useState(event?.reminderDaysBefore?.toString() ?? '')
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    try {
      await save.mutateAsync({
        id: event?.id,
        input: {
          title: title.trim(),
          description: description.trim() || null,
          kind,
          location: location.trim() || null,
          audienceAll,
          inviteeIds: audienceAll ? [] : inviteeIds,
          groupIds: audienceAll ? [] : groupIds,
          dates: dates
            .filter((d) => d)
            .map((d) => ({ startsAt: new Date(d).toISOString(), endsAt: null, allDay: false })),
          responseDeadline: deadline ? new Date(`${deadline}T23:59`).toISOString() : null,
          publishAt: publishAt ? new Date(`${publishAt}T07:00`).toISOString() : null,
          notifyPush,
          notifySms,
          reminderDaysBefore: reminder ? Number(reminder) : null
        }
      })

      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Begivenheden kunne ikke gemmes.')
    }
  }

  return (
    <Card title={event ? 'Ret begivenheden' : 'Ny begivenhed'}>
      <form className="stack" onSubmit={submit}>
        <div className="form-grid">
          <label className="field">
            <span className="field__label">Navn *</span>
            <input required maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>

          <label className="field">
            <span className="field__label">Hvad er det?</span>
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              {(kinds ?? []).map((k) => (
                <option key={k.code} value={k.code}>
                  {k.icon} {k.name}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">Hvor</span>
            <input maxLength={300} value={location} onChange={(e) => setLocation(e.target.value)} />
          </label>

          <label className="field">
            <span className="field__label">Svar senest</span>
            <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
          </label>

          <label className="field">
            <span className="field__label">Udgives</span>
            <input type="date" value={publishAt} onChange={(e) => setPublishAt(e.target.value)} />
            <span className="tiny muted">Tom = med det samme. Indtil da kan kun du og kontoret se den.</span>
          </label>

          <label className="field">
            <span className="field__label">Mind om den</span>
            <select value={reminder} onChange={(e) => setReminder(e.target.value)}>
              <option value="">Ingen påmindelse</option>
              <option value="1">Dagen før</option>
              <option value="2">2 dage før</option>
              <option value="3">3 dage før</option>
              <option value="7">En uge før</option>
              <option value="14">14 dage før</option>
            </select>
          </label>
        </div>

        <label className="field">
          <span className="field__label">Beskrivelse</span>
          <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>

        <div className="field">
          <span className="field__label">Datoer at vælge imellem *</span>
          <div className="stack" style={{ gap: 6 }}>
            {dates.map((date, index) => {
              // Dato og klokkeslaet staar hver for sig: klokkeslaettet er en
              // rulleliste i halve timer, saa det kan vaelges med tommelfingeren
              // i stedet for at tastes ind.
              const [day, time] = splitDate(date)

              return (
                <div key={index} className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                  <input
                    type="date"
                    required
                    value={day}
                    onChange={(e) =>
                      setDates((current) =>
                        current.map((d, i) => (i === index ? `${e.target.value}T${splitDate(d)[1]}` : d))
                      )
                    }
                  />

                  <select
                    required
                    style={{ maxWidth: 120 }}
                    value={time}
                    onChange={(e) =>
                      setDates((current) =>
                        current.map((d, i) => (i === index ? `${splitDate(d)[0]}T${e.target.value}` : d))
                      )
                    }
                  >
                    {/* Et skaevt klokkeslaet fra en gammel begivenhed skal kunne
                        blive staaende, selv om det ikke er en halv time. */}
                    {!TIMES.includes(time) && <option value={time}>{time}</option>}
                    {TIMES.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>

                  {dates.length > 1 && (
                    <button
                      type="button"
                      className="btn btn--sm"
                      onClick={() => setDates((current) => current.filter((_, i) => i !== index))}
                    >
                      Fjern
                    </button>
                  )}
                </div>
              )
            })}
          </div>
          <button
            type="button"
            className="btn btn--sm"
            style={{ marginTop: 6, alignSelf: 'flex-start' }}
            onClick={() => setDates((current) => [...current, defaultDate()])}
          >
            + Foreslå en dato mere
          </button>
          <span className="tiny muted">
            Flere datoer bliver til en afstemning: folk krydser af, hvornår de kan, og du vælger til sidst.
          </span>
        </div>

        <label className="row small" style={{ gap: 8 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={audienceAll}
            onChange={(e) => setAudienceAll(e.target.checked)}
          />
          Hele huset er inviteret
        </label>

        {!audienceAll && groups && groups.length > 0 && (
          <div className="field">
            <span className="field__label">
              Arbejdsgrupper {groupIds.length > 0 && <span className="tiny muted">({groupIds.length} valgt)</span>}
            </span>
            <div className="cal-people">
              {groups.map((group) => (
                <label key={group.id} className={`cal-person ${groupIds.includes(group.id) ? 'cal-person--on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={groupIds.includes(group.id)}
                    onChange={() =>
                      setGroupIds((current) =>
                        current.includes(group.id) ? current.filter((x) => x !== group.id) : [...current, group.id]
                      )
                    }
                  />
                  <span>
                    {group.icon ? `${group.icon} ` : ''}
                    {group.name} ({group.memberCount})
                  </span>
                </label>
              ))}
            </div>
            <span className="tiny muted">Hele gruppen inviteres - også dem, der kommer til senere.</span>
          </div>
        )}

        {!audienceAll && (
          <div className="field">
            <span className="field__label">
              Enkelte kolleger {inviteeIds.length > 0 && <span className="tiny muted">({inviteeIds.length} valgt)</span>}
            </span>
            <div className="cal-people">
              {(users ?? []).map((u) => (
                <label key={u.id} className={`cal-person ${inviteeIds.includes(u.id) ? 'cal-person--on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={inviteeIds.includes(u.id)}
                    onChange={() =>
                      setInviteeIds((current) =>
                        current.includes(u.id) ? current.filter((x) => x !== u.id) : [...current, u.id]
                      )
                    }
                  />
                  <span>{u.displayName}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <label className="row small" style={{ gap: 8 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={notifyPush}
            onChange={(e) => setNotifyPush(e.target.checked)}
          />
          Giv besked i appen - når den udgives, når datoen er valgt, og ved påmindelsen
        </label>

        <label className="row small" style={{ gap: 8 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={notifySms}
            onChange={(e) => setNotifySms(e.target.checked)}
          />
          Send også som SMS
        </label>

        {error && <div className="error-box">{error}</div>}

        <div className="btn-group">
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Gemmer ...' : event ? 'Gem' : 'Opret begivenhed'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Fortryd
          </button>
        </div>
      </form>
    </Card>
  )
}

/**
 * Klokkeslættene i rullelisten: hele og halve timer hele døgnet.
 *
 * <b>En liste og ikke et tastefelt.</b> Et `datetime-local` på en computer skal
 * have tallene tastet i små felter, og det er dét, der driller, når man bare vil
 * flytte en fest fra 17 til 18.
 */
const TIMES = Array.from({ length: 48 }, (_, index) => {
  const hours = String(Math.floor(index / 2)).padStart(2, '0')
  return `${hours}.${index % 2 === 0 ? '00' : '30'}`.replace('.', ':')
})

/** Deler "2026-12-04T17:00" op i dato og klokkeslæt. Tomt felt giver et brugbart udgangspunkt. */
function splitDate(value: string): [string, string] {
  const [day = '', time = '17:00'] = value.split('T')
  return [day, time.slice(0, 5) || '17:00']
}

/** I morgen kl. 17 - et gæt, der som regel passer til en fest. */
function defaultDate() {
  const date = new Date()
  date.setDate(date.getDate() + 7)
  date.setHours(17, 0, 0, 0)
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
}

/** Kollegerne med billede, telefon og mail. Tallene kommer fra Microsoft 365 (beslutning 041). */
export function PhoneList() {
  const { data: users } = useUsers()
  const [search, setSearch] = useState('')
  // Kontoret kan skrive ansaettelsesdatoer til jubilaeerne her (beslutning 117).
  const { isIntranetAdmin } = useAuth()
  // Jubilæer og velkomst kan være slået fra hver for sig (beslutning 119).
  const moduleEnabled = useModuleEnabled()
  const celebrationsOn = moduleEnabled('intranet_celebrations')
  const welcomeOn = moduleEnabled('intranet_welcome')
  const [editingDates, setEditingDates] = useState(false)
  const hireDates = useHireDateMap(isIntranetAdmin && editingDates && celebrationsOn)
  // "Byd velkommen" til nye (beslutning 118) - kun kontoret.
  const { data: welcomed } = useWelcomeStatus(isIntranetAdmin && welcomeOn)
  const welcomedIds = new Set((welcomed ?? []).map((w) => w.userId))
  const [welcoming, setWelcoming] = useState<UserRef | null>(null)
  const [editingChecklist, setEditingChecklist] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const query = search.trim().toLowerCase()
  const shown = (users ?? []).filter(
    (u) =>
      !query ||
      u.displayName.toLowerCase().includes(query) ||
      (u.email ?? '').toLowerCase().includes(query) ||
      (u.phoneNumber ?? '').includes(query)
  )

  return (
    <Card title={`Telefonliste (${shown.length})`}>
      <div className="row" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          style={{ maxWidth: 260 }}
          placeholder="Søg efter en kollega ..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {isIntranetAdmin && (celebrationsOn || welcomeOn) && (
          <button type="button" className="btn btn--sm" onClick={() => setEditingDates((v) => !v)}>
            {editingDates ? 'Færdig' : 'Datoer og velkomst'}
          </button>
        )}
        {isIntranetAdmin && welcomeOn && (
          <button type="button" className="btn btn--sm" onClick={() => setEditingChecklist((v) => !v)}>
            Velkomst-tjekliste
          </button>
        )}
      </div>

      {notice && <div className="map-notice" style={{ marginBottom: 12 }}>{notice}</div>}
      {editingChecklist && (
        <div style={{ marginBottom: 12 }}>
          <WelcomeChecklistEditor onClose={() => setEditingChecklist(false)} />
        </div>
      )}
      {welcoming && (
        <div style={{ marginBottom: 12 }}>
          <WelcomeForm
            user={welcoming}
            onClose={(message) => {
              setWelcoming(null)
              if (message) setNotice(message)
            }}
          />
        </div>
      )}
      {editingDates && (
        <div className="tiny muted" style={{ marginBottom: 8 }}>
          Datoen gemmes, når du går ud af feltet. Den bruges til jubilæer.
        </div>
      )}

      <div className="stack" style={{ gap: 2 }}>
        {shown.map((user) => (
          <div key={user.id} className="row row--between device-row" style={{ gap: 10 }}>
            <div className="row" style={{ gap: 10, minWidth: 0 }}>
              <Avatar user={user} size={36} />
              <div style={{ minWidth: 0 }}>
                <div className="small">{user.displayName}</div>
                <div className="tiny muted">{user.email}</div>
              </div>
            </div>

            {editingDates ? (
              <div className="row" style={{ gap: 8, alignItems: 'flex-end' }}>
                {celebrationsOn && <HireDateInput user={user} value={hireDates.get(user.id)} />}
                {!welcomeOn ? null : welcomedIds.has(user.id) ? (
                  <span className="tiny muted">👋 Budt velkommen</span>
                ) : (
                  <button type="button" className="btn btn--sm" onClick={() => { setNotice(null); setWelcoming(user) }}>
                    👋 Byd velkommen
                  </button>
                )}
              </div>
            ) : user.phoneNumber ? (
              <a className="btn btn--sm" href={`tel:${user.phoneNumber.replace(/\s/g, '')}`}>
                📞 {user.phoneNumber}
              </a>
            ) : (
              <span className="tiny muted">Intet nummer</span>
            )}
          </div>
        ))}
      </div>

      {shown.length === 0 && <div className="small muted">Ingen kolleger matcher søgningen.</div>}
    </Card>
  )
}

/** En genvej som et kort man kan trykke på. Åbner i en ny fane. */
export function LinkTile({ link, onEdit }: { link: { id: number; title: string; url: string; description?: string | null; icon?: string | null; isActive: boolean }; onEdit?: () => void }) {
  return (
    <div className="card card--flat" style={{ padding: 12, opacity: link.isActive ? 1 : 0.5 }}>
      <div className="row row--between" style={{ gap: 8 }}>
        <a href={link.url} target="_blank" rel="noreferrer" style={{ minWidth: 0 }}>
          <div className="small">
            {link.icon ? `${link.icon} ` : '🔗 '}
            <strong>{link.title}</strong>
          </div>
          {link.description && <div className="tiny muted">{link.description}</div>}
          <div className="tiny muted">{new URL(link.url).hostname}</div>
        </a>
        {onEdit && (
          <button className="btn btn--sm" onClick={onEdit}>
            Ret
          </button>
        )}
      </div>
    </div>
  )
}

/** Et opslag i en liste, der linker videre til dets egen side. */
export function PostLine({ post }: { post: IntranetPost }) {
  return (
    <Link to={`/intranet/opslag/${post.id}`} className="row row--between device-row">
      <div style={{ minWidth: 0 }}>
        <div className="small">
          {post.isPinned ? '📌 ' : ''}
          <strong>{post.title}</strong>
        </div>
        <div className="tiny muted">
          {post.createdBy?.displayName ?? 'Overblik'} · {formatDate(post.createdAt)}
        </div>
      </div>
      {post.requiresAck && !post.hasRead && <span className="tiny warn">Skal læses</span>}
    </Link>
  )
}
